import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { FaUserCircle, FaShoppingCart, FaBell, FaBoxOpen } from "react-icons/fa";
import { BarChart } from "../../components/DashboardCharts"; // Import new chart
import "./Dashboard.css";

const LOCAL_API_BASE_URL = "http://localhost:5000";
const RENDER_API_BASE_URL = "https://wecareehs-backend.onrender.com";
const API_BASE_URL = process.env.REACT_APP_USE_RENDER === "true" ? RENDER_API_BASE_URL : LOCAL_API_BASE_URL;

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/dashboard`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch dashboard data");
        }
        const data = await response.json();
        setDashboardData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  if (loading) return <div className="training-loading">Loading dashboard...</div>;
  if (error) return <div className="training-loading">Error: {error}</div>;

  // Prepare Chart Data
  const orderStats = [
    { label: 'Pending', value: dashboardData?.stats?.pendingOrders || 0, color: '#f1c40f' },
    { label: 'Completed', value: dashboardData?.stats?.completedOrders || 0, color: '#2ecc71' },
    { label: 'Total', value: dashboardData?.stats?.totalOrders || 0, color: '#3b82f6' }
  ];

  return (
    <div className="training-dashboard">
      <aside className="training-sidebar">
        <div className="training-sidebar-header">
          <h1 className="training-logo">WeCareEHS</h1>
        </div>
        <nav>
          <ul className="training-nav-list">
            <li className="training-nav-item active">
              <a href="/dashboard" className="training-nav-link">
                <FaUserCircle className="training-nav-icon" />
                <span>Overview</span>
              </a>
            </li>
            <li className="training-nav-item">
              <a href="/cart" className="training-nav-link">
                <FaShoppingCart className="training-nav-icon" />
                <span>My Cart</span>
              </a>
            </li>
            <li className="training-nav-item">
              <a href="/feedback" className="training-nav-link">
                <FaBell className="training-nav-icon" />
                <span>Feedback</span>
              </a>
            </li>
          </ul>
        </nav>
        <button className="training-logout-button" onClick={() => {
          localStorage.clear();
          window.location.href = "/login";
        }}>
          Logout
        </button>
      </aside>

      <main className="training-main">
        <header className="training-header">
          <div className="training-welcome">
            <h2 className="training-welcome-title">Welcome back, {user?.username || "User"}</h2>
            <p className="training-welcome-subtitle">Here is your training overview</p>
          </div>
        </header>

        <div className="dashboard-grid">
          {/* Left Column: Stats Cards */}
          <div className="stats-column">
            <section className="training-stats-container">
              <div className="training-stat-card">
                <div className="training-stat-icon"><FaBoxOpen /></div>
                <div className="training-stat-content">
                  <h3 className="training-stat-title">Total Orders</h3>
                  <p className="training-stat-value">{dashboardData?.stats?.totalOrders || 0}</p>
                </div>
              </div>
              <div className="training-stat-card">
                <div className="training-stat-icon" style={{ color: '#f1c40f' }}><FaBell /></div>
                <div className="training-stat-content">
                  <h3 className="training-stat-title">Pending</h3>
                  <p className="training-stat-value">{dashboardData?.stats?.pendingOrders || 0}</p>
                </div>
              </div>
              <div className="training-stat-card">
                <div className="training-stat-icon" style={{ color: '#2ecc71' }}><FaShoppingCart /></div>
                <div className="training-stat-content">
                  <h3 className="training-stat-title">Completed</h3>
                  <p className="training-stat-value">{dashboardData?.stats?.completedOrders || 0}</p>
                </div>
              </div>
            </section>

            <div className="quick-actions-card">
              <h3>Quick Actions</h3>
              <div className="action-buttons">
                <a href="/training" className="action-btn primary">Browse Courses</a>
                <a href="/profile" className="action-btn secondary">Edit Profile</a>
              </div>
            </div>
          </div>

          {/* Right Column: Analytics */}
          <div className="analytics-column">
            <div className="analytics-wrapper">
              <BarChart
                title="Order Activity"
                data={orderStats}
                color="#FF6B00"
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
